import React, { useState, useEffect } from "react";
import {
    Card,
    CardBody,
    Row,
    Col,
    Label,
    Input,
    Badge,
    Spinner,
    Button,
    Alert,
} from "reactstrap";
import {
    LineChart,
    Line,
    Tooltip,
    CartesianGrid,
    XAxis,
    YAxis,
    Legend,
    ResponsiveContainer,
}
 from "recharts";
import axiosInstance from "../../components/AxiosInstance";
import { toast } from "react-toastify";
import Compressor from "../Compressor";

// Function to format today's date as YYYY-MM-DD
const getTodayDate = () => {
    return new Date().toISOString().split('T')[0];
};


const RESOLUTIONS = {
    'hour': '1m',
    '8hour': '1m',
    'day': '10m',
    'week': '1h',
    'month': '1d' // Use daily resolution for monthly data
}; 

// Helper function to generate an array of the last N months (e.g., last 12 months)
const getLastMonths = (count = 12) => {
    const months = [];
    let date = new Date();
    date.setDate(1); // Set to the first day of the current month
    date.setHours(0, 0, 0, 0);

    for (let i = 0; i < count; i++) {
        // Format YYYY-MM-01 for API date parameter
        const value = date.toISOString().split('T')[0]; 
        
        // Format for display (e.g., Dec 2025)
        const display = date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
        
        months.push({ value, display });
        
        // Move to the previous month
        date.setMonth(date.getMonth() - 1);
    }
    return months;
};


// Function to format the X-Axis ticks
const xAxisTickFormatter = (timestamp, range) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    
    // Show HH:MM for short time ranges (Hour, 8Hour, Day) <--- MODIFIED LOGIC
    if (range === 'hour' || range === '8hour' || range === 'day') { 
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${hours}:${minutes}`;
    }
    // Show DD/MM for Week and Month 
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${day}/${month}`;
};


const PerformanceTrend = ({compressorId }) => {
    // --- State Management ---

    const HARDCODED_COMPRESSOR_ID = compressorId;
    console.log("PerformanceTrend initialized with machineId:", compressorId);
    const [parameterMap, setParameterMap] = useState({});
    const [availableParameters, setAvailableParameters] = useState([]);
    const colors = ["#ff5255", "#006cd1", "#326916", "#a3771e", "#421f72"];
    const [selectedParams, setSelectedParams] = useState([]);
    const [showParamDropdown, setShowParamDropdown] = useState(false);
    const [selectedRange, setSelectedRange] = useState("hour");
    const [selectedDate, setSelectedDate] = useState(getTodayDate());
    
    // ⭐ NEW STATE: To store the 'YYYY-MM-01' string for the selected month
    const [selectedMonthStart, setSelectedMonthStart] = useState(getLastMonths()[0]?.value || getTodayDate()); 
    const [graphData, setGraphData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingParams, setLoadingParams] = useState(true);
    const [paramError, setParamError] = useState(null);

    const isToday = selectedDate === getTodayDate();
    
    // ⭐ Monthly options generated on component load
    const monthOptions = getLastMonths(24);

    const timeRanges = [
        { value: "hour", label: "Last Hour", show: isToday },
        { value: "8hour", label: "Last 8 Hours", show: isToday },
        { value: "day", label: "Day" , show: true },
        { value: "week", label: "Last Week", show: true },
        { value: "month", label: "Month", show: true }, // Changed label from 'Last Month' to 'Month'
    ];

    // --- Event Handler ---
    const handleParamSelect = (id) => {
        let updated;
        if (selectedParams.includes(id)) {
            updated = selectedParams.filter((p) => p !== id);
        } else {
            if (selectedParams.length >= 5) {
                alert("You can select up to 5 parameters only.");
                return;
            }
            updated = [...selectedParams, id];
        }
        setSelectedParams(updated);
    };

    const handleRangeChange = (range) => {
        setSelectedRange(range);
        // Reset selectedDate to today if not looking at specific month
        if (range !== 'month') {
             setSelectedDate(getTodayDate());
        }
    };


    // --- Helper Functions ---
    const transformApiDataToChartData = (records, map) => {
        return records.map((record) => {
            const fullTimestamp = `${record.date} ${record.time}`; 
            
            const chartPoint = {
                timestamp: fullTimestamp 
            };

            Object.keys(record.values).forEach(paramName => {
                const param = Object.values(map).find(p => p.name === paramName);
                if (param) {
                    let value = record.values[paramName];
                    
                    if (value === '<nil>') {
                        value = 0;
                    }

                    chartPoint[param.key] = parseFloat(value) || 0;
                }
            });
            return chartPoint;
        });
    };
   const authToken = localStorage.getItem("access");
    const fetchLogData = async (suppressLoading = false) => {
        if (selectedParams.length === 0) {
            setGraphData([]);
            return;
        }

        if (!suppressLoading) setLoading(true);
        
        try {
            const parameterNames = selectedParams
                .map(id => parameterMap[id]?.name)
                .filter(name => name)
                .join(',');

            if (!parameterNames) {
                if (!suppressLoading) setLoading(false);
                return;
            }
            
            // ⭐ CORE LOGIC: Determine 'date' parameter based on 'selectedRange'
            let dateParam;
            if (selectedRange === 'month') {
                // For 'month', use the first day of the selected month
                dateParam = selectedMonthStart;
            } else {
                // For 'hour', '8hour', 'day', 'week', use the standard selectedDate
                dateParam = selectedDate;
            }

            const dataResolution = RESOLUTIONS[selectedRange] || '1m'; 

            const apiUrl = `https://api.hvacsync.in/api/compressor/logs/?compressor=${HARDCODED_COMPRESSOR_ID}&parameters=${parameterNames}&range=${selectedRange}&date=${dateParam}`;

            const res = await axiosInstance.get(apiUrl,
                 {
                    headers: {
                    Authorization: `Bearer ${authToken}`, // Replace with your actual token
                 }
        },

            );

            const rawRecords = res.data?.records || [];
            const transformedData = transformApiDataToChartData(rawRecords, parameterMap);

            setGraphData(transformedData); 

        } catch (err) {
            console.error("Error fetching log data:", err);
            setGraphData([]);
            if (!suppressLoading) 
                toast.error("Failed to fetch log data. Check console for details.");

        } finally {
            if (!suppressLoading) setLoading(false);
        }
    };
    
    // --- Export to Excel (CSV format) - Left unchanged ---
    const handleExportToExcel = () => {
        if (graphData.length === 0) {
            alert("No data available to export.");
            return;
        }

        const allKeys = new Set(['timestamp']);
        graphData.forEach(item => {
            Object.keys(item).forEach(key => allKeys.add(key));
        });
        
        const headerMap = {};
        Object.values(parameterMap).forEach(p => {
            headerMap[p.key] = p.name;
        });

        const headers = Array.from(allKeys);
        const headerRow = headers.map(key => {
            return key === 'timestamp' ? 'Timestamp' : headerMap[key] || key;
        }).join(',');

        const csvBody = graphData.map(row => {
            return headers.map(key => {
                return row[key] !== undefined ? row[key] : '';
            }).join(',');
        }).join('\n'); 

        const csvString = `${headerRow}\n${csvBody}`;
        
        const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        
        link.setAttribute('href', url);
        const filename = `Performance_Trend_${selectedDate}_${selectedRange}.csv`;
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };


    // ----------------------------------------------
    // 1️⃣ FETCH PARAMETERS (Initial Load - Unchanged)
    // ----------------------------------------------
   // ----------------------------------------------
    // 1️⃣ FETCH PARAMETERS (Modified Selection Logic)
    // ----------------------------------------------
    useEffect(() => {
        const fetchAndMapParams = async () => {
            setLoadingParams(true);
            setParamError(null);
            try {
                const res = await axiosInstance.get(`https://api.hvacsync.in/api/compressor/parameters/`, {
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                    }
                });
                
                const rawParameters = res.data?.data || [];
                const newParameterMap = {};
                const newAvailableParameters = rawParameters.map((p) => {
                    const id = p.id;
                    const name = p.name;
                    const key = name.replace(/\s+/g, "_").replace(/\W/g, "");
                    const param = { id, name, key };
                    newParameterMap[id] = param;
                    return param;
                });

                setParameterMap(newParameterMap);
                setAvailableParameters(newAvailableParameters);

                // ⭐ LOGIC: Check for "MOTOR CURR"
                const motorCurrParam = newAvailableParameters.find(p => 
                    p.name.toUpperCase().includes("MOTOR CURR")
                );

                if (motorCurrParam) {
                    // If Motor Current is found, select ONLY that one
                    setSelectedParams([motorCurrParam.id]);
                } else {
                    // Fallback: Select first 5 if Motor Current isn't available
                    const initialSelectedIds = newAvailableParameters.slice(0, 5).map(p => p.id);
                    setSelectedParams(initialSelectedIds);
                }

            } catch (err) {
                console.error(`Error loading parameters:`, err);
                setParamError("Failed to load parameters.");
                setAvailableParameters([]);
                setParameterMap({});
                setSelectedParams([]);
            } finally {
                setLoadingParams(false);
            }
        };
        fetchAndMapParams();
    }, []);

    // ----------------------------------------------
    // 2️⃣ EFFECT for Data Fetch/Filter Change
    // ----------------------------------------------
    useEffect(() => {
        // Ensure range is valid for the current date selection
        const availableRanges = timeRanges.filter(r => r.show).map(r => r.value);
        if (!availableRanges.includes(selectedRange) && selectedRange !== 'month' && !isToday) {
            setSelectedRange('day'); 
            return;
        }

        // Only fetch if required parameters are available (selectedMonthStart is relevant for 'month' range)
        if (selectedRange === 'month' && !selectedMonthStart) return;
        
        fetchLogData();
    }, [selectedParams, selectedRange, selectedDate, selectedMonthStart, parameterMap]);


    // ----------------------------------------------
    // 3️⃣ POLLING FOR REAL-TIME DATA (Set to 10-second interval)
    // ----------------------------------------------
    useEffect(() => {
        let intervalId;
        const isPollingRange = isToday && (selectedRange === 'hour' || selectedRange === '8hour');

        if (isPollingRange && selectedParams.length > 0) {
            intervalId = setInterval(() => {
                fetchLogData(true); 
            }, 60000); // 10 seconds
        }

        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
    }, [isToday, selectedRange, selectedParams, parameterMap, selectedDate]); 


    // --- JSX (Chart Display) ---
    return (
        <Card>
            <CardBody>
                <h3 className="fw-bold text-primary mb-3">Performance Trend</h3>

                {/* --- CONFIGURATION --- */}
                <Row className="mb-3 align-items-end">
                    
                    {/* Parameter Selector (md=4) */}
                    <Col md={4} className="position-relative">
                        <Label>Parameters (Max 5)</Label>

                        {loadingParams ? (
                            <div className="form-control text-center"><Spinner size="sm" /> Loading Parameters...</div>
                        ) : paramError ? (
                            <Alert color="danger" className="py-2 mb-0">{paramError}</Alert>
                        ) : (
                            <div
                                className="form-control"
                                style={{ cursor: "pointer" }}
                                onClick={() => setShowParamDropdown(!showParamDropdown)}
                            >
                                {availableParameters.length === 0
                                    ? "No parameters found"
                                    : selectedParams.length === 0
                                        ? "Select Parameters"
                                        : `${selectedParams.length} selected`}
                            </div>
                        )}

                        {showParamDropdown && !loadingParams && availableParameters.length > 0 && (
                            <div
                                style={{
                                    border: "1px solid #ccc",
                                    borderRadius: "8px",
                                    background: "white",
                                    position: "absolute",
                                    width: "100%",
                                    maxHeight: "200px",
                                    overflowY: "auto",
                                    zIndex: 1000,
                                    marginTop: '4px',
                                    padding: '8px',
                                }}
                            >
                                {availableParameters.map((p) => (
                                    <div
                                        key={p.id}
                                        className="p-2 d-flex align-items-center hover-bg-light"
                                        onClick={() => handleParamSelect(p.id)}
                                        style={{ cursor: "pointer", borderRadius: '4px' }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedParams.includes(p.id)}
                                            onChange={() => handleParamSelect(p.id)}
                                            onClick={(e) => e.stopPropagation()}
                                            className="form-check-input me-2"
                                        />
                                        <span className="ms-2">{p.name}</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Selected Chips */}
                        <div className="mt-2 d-flex flex-wrap">
                            {selectedParams.map((id, index) => {
                                const param = parameterMap[id];
                                if (!param) return null;
                                return (
                                    <Badge
                                        pill
                                        key={id}
                                        color="primary"
                                        className="me-2 mb-1"
                                        style={{ cursor: "pointer", backgroundColor: colors[index % colors.length] }}
                                        onClick={() => setSelectedParams(selectedParams.filter((x) => x !== id))}
                                    >
                                        {param.name} &times;
                                    </Badge>
                                );
                            })}
                        </div>
                    </Col>

                    {/* Time Range Dropdown (md=2) */}
                    <Col md={2}>
                        <Label>Time Range</Label>
                        <Input
                            type="select"
                            value={selectedRange}
                            onChange={(e) => handleRangeChange(e.target.value)} // Use custom handler
                        >
                            {/* Filter based on the 'show' property */}
                            {timeRanges.filter(r => r.show).map(r => (
                                <option key={r.value} value={r.value}>
                                    {r.label}
                                </option>
                            ))}
                        </Input>
                    </Col>

                    {/* ⭐ CONDITIONAL DATE/MONTH PICKER (md=2) */}
                    <Col md={2}>
                        {selectedRange === 'month' ? (
                            <>
                                <Label>Select Month</Label>
                                <Input
                                    type="select"
                                    value={selectedMonthStart}
                                    onChange={(e) => setSelectedMonthStart(e.target.value)}
                                >
                                    {monthOptions.map(m => (
                                        <option key={m.value} value={m.value}>
                                            {m.display}
                                        </option>
                                    ))}
                                </Input>
                            </>
                        ) : (
                            <>
                                <Label>Date</Label>
                                <Input
                                    type="date"
                                    value={selectedDate}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                />
                            </>
                        )}
                    </Col>

                    {/* Fetch Data Button (md=2) */}
                    <Col md={2}>
                        <Button
                            color="primary"
                            onClick={() => fetchLogData(false)}
                            disabled={loading || selectedParams.length === 0}
                            className="w-100"
                            style={{marginTop: '30px'}}
                        >
                            {loading ? <Spinner size="sm" /> : "Fetch Trend"}
                        </Button>
                    </Col>
                    
                    {/* Export Button (md=2) */}
                    <Col md={2}>
                        <Button
                            color="success"
                            onClick={handleExportToExcel}
                            disabled={graphData.length === 0}
                            className="w-100"
                            style={{marginTop: '30px'}}
                        >
                            Export to Excel (CSV)
                        </Button>
                    </Col>
                </Row>

                <hr />

                {/* --- CHART DISPLAY --- */}
                <div className="mt-4">
                    {loading ? (
                        <div className="text-center p-5"><Spinner color="primary" /> <p>Loading trend data...</p></div>
                    ) : (
                        graphData.length > 0 ? (
                            <Row className="mb-3">
                                <Col md={12}>
                                    <ResponsiveContainer width="100%" height={400}>
                                        <LineChart data={graphData} margin={{ top: 5, right: 30, left: 20, bottom: 10 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
                                            <XAxis
                                                dataKey="timestamp"
                                                tickFormatter={(timestamp) => xAxisTickFormatter(timestamp, selectedRange)} 
                                                angle={-25} 
                                                textAnchor="end"
                                                interval="preserveStartEnd"
                                                label={{
                                                    // Now includes 'day' to display Time
                                                    value: selectedRange === 'hour' || selectedRange === '8hour' || selectedRange === 'day' ? 'Time of Day (HH:MM)' : 'Date (DD/MM)',
                                                    position: 'bottom',
                                                    offset: 19
                                                }}
                                            />
                                            <YAxis />
                                            <Tooltip 
                                                // Updated label formatter to show full date/time for shorter ranges
                                                labelFormatter={(label) => {
                                                    const date = new Date(label);
                                                    if (selectedRange === 'hour' || selectedRange === '8hour' || selectedRange === 'day') {
                                                        return `Time: ${date.toLocaleTimeString()} (${date.toLocaleDateString()})`;
                                                    }
                                                    return `Date: ${date.toLocaleDateString()}`;
                                                }}
                                                formatter={(value, name) => [value.toFixed(2), name]} 
                                            /> 
                                            <Legend />
                                            {selectedParams.map((id, index) => {
                                                const p = parameterMap[id];
                                                if (!p) return null;
                                                return (
                                                    <Line
                                                        key={p.id} 
                                                        dataKey={p.key}
                                                        name={p.name}
                                                        stroke={colors[index % colors.length]}
                                                        strokeWidth={3}
                                                        type="monotone"
                                                        dot={false}
                                                    />
                                                );
                                            })}
                                        </LineChart>
                                    </ResponsiveContainer>
                                </Col>
                            </Row>
                        ) : (
                            <div className="text-center p-5 text-muted">
                                {selectedParams.length > 0
                                    ? "No data available for the selected parameters, range, or date. Try a different selection."
                                    : "Please select at least one Parameter to plot the trend."}
                            </div>
                        )
                    )}
                </div>
            </CardBody>
        </Card>
    );
};

export default PerformanceTrend;